<template>
  <g :transform="`translate(${contourExtent.x}, ${-contourExtent.y})`" :class="layerType">
    <g class="contours"></g>
  </g>
</template>

<script>
import config from '@/config.js';

const DECIMAL_POINTS = 1;
const DECIMAL_POINTS_MULTIPLIER = Math.pow(10, DECIMAL_POINTS);
export default {
  props: {
    layerType: { type: String, required: true },
    thingsSpotList: { type: Array, default: null },
    x: { type: Number, default: null },
    y: { type: Number, default: null },
    width: { type: Number, default: null },
    height: { type: Number, default: null },
  },
  data() {
    return {
      d3: null,
    };
  },
  computed: {
    targetList() {
      return this.thingsSpotList || this.$store.state.visualization.layers[this.layerType].targetList;
    },
    maxValue:     ({ $store, layerType }) => $store.state.visualization.layers[layerType].maxValue,
    minValue:     ({ $store, layerType }) => $store.state.visualization.layers[layerType].minValue,
    lineInterval: ({ $store, layerType }) => $store.state.visualization.layers[layerType].lineInterval,
    currentBuildingId: ({ $store }) => $store.state.current.buildingId,
    currentBuilding: ({ $store, currentBuildingId }) => $store.getters['area/buildingFind'](currentBuildingId),
    visualizationConfig:({ layerType }) => config.visualizationHeaders[layerType],
    colors: (vm) => vm.visualizationConfig.colors,
    calcPointNumberX: ({ contourExtent }) => contourExtent.width * DECIMAL_POINTS_MULTIPLIER + 1,
    calcPointNumberY: ({ contourExtent }) => contourExtent.height * DECIMAL_POINTS_MULTIPLIER + 1,
    targetCalcPoints() {
      const positions = [];
      const contourExtentX = parseInt(this.contourExtent.x * DECIMAL_POINTS_MULTIPLIER);
      const contourExtentY = parseInt(this.contourExtent.y * DECIMAL_POINTS_MULTIPLIER);
      const height = contourExtentY + parseInt(this.contourExtent.height * DECIMAL_POINTS_MULTIPLIER);
      const width =  contourExtentX + parseInt(this.contourExtent.width * DECIMAL_POINTS_MULTIPLIER);

      for (let y = contourExtentY; y <= height; ++y) {
        for (let x = contourExtentX; x <= width; ++x) {
          positions.push({ positionX: x / DECIMAL_POINTS_MULTIPLIER, positionY: y / DECIMAL_POINTS_MULTIPLIER })
        }
      }
      return positions;
    },
    contourExtent() {
      let x = this.x || 0;
      let y = this.y || 0;
      let width = this.width || this.currentBuilding.width;
      let height = this.height || this.currentBuilding.depth;

      return {
        x:      x,
        y:      y,
        width:  +width.toFixed(DECIMAL_POINTS),
        height: +height.toFixed(DECIMAL_POINTS),
      };
    },
  },
  watch: {
    targetList(value) {
      if (value.length == 0) this.$store.commit('dialog/startLoad');
      setTimeout(() => { this.$store.commit('dialog/finishLoad') }, 1000);
      this.reflowContour();
    },
  },
  mounted() {
    const elementSelector = `.${this.layerType}>.contours`;

    this.d3 = d3.select(this.$el.querySelector(elementSelector));
    this.createContour();
  },
  methods: {
    domainColor() {
      const colorCount = this.colors.length;
      const valueRange = this.maxValue - this.minValue;
      const step = valueRange / (colorCount - 1);
      
      const domain = [];
      for (let i = 0; i < colorCount; i++) {
        domain.push(this.minValue + (step * i));
      }
      return domain;
    },
    createContour() {
      const scale = this.contourExtent.width / this.calcPointNumberX;

      this.color = d3.scaleLinear()
        .domain(this.domainColor())
        .range(this.colors)
      ;

      this.d3.selectAll('path')
        .data(this.convertToContour())
        .enter()
        .append('path')
        .attr('d', d3.geoPath(d3.geoIdentity().scale(scale)))
        .attr('fill', (d) => this.color(d.value))
      ;
    },
    removeContour() {
      this.d3.selectAll('*').remove();
    },
    reflowContour() {
      this.removeContour();
      this.createContour();
    },
    convertToContour() {
      return d3.contours()
        .size([this.calcPointNumberX, this.calcPointNumberY])
        .thresholds(this.setThresholds())
        (this.calcContourValues())
      ;
    },
    setThresholds() {
      return this.thresholds = d3.range(this.minValue, this.maxValue, this.lineInterval);
    },
    calcContourValues() {
      return this.targetCalcPoints.map((targetCalcPoint) => {
        return this.calcValueWithPoint(targetCalcPoint);
      });
    },
    calcValueWithPoint(targetCalcPoint) {
      if (this.targetList.length == 0) return this.minValue - 1;

      let denominator = 0.0;
      let numerator = 0.0;
      let calculatedValue = 0;

      this.targetList.some((targetContourThing) => {
        const distance = this.calculateDistance(
          targetCalcPoint.positionX,
          targetCalcPoint.positionY,
          targetContourThing.thingPositions[0].positionX,
          targetContourThing.thingPositions[0].positionY
        )

        if (distance === 0) {
          calculatedValue = targetContourThing.thingState.reported[this.layerType];
          denominator = 0;
          return true;
        }

        let squaredDistance = Math.pow(distance, 2);
        numerator += targetContourThing.thingState.reported[this.layerType] / squaredDistance;
        denominator += 1 / squaredDistance;
      });

      if (denominator !== 0) {
        calculatedValue = numerator / denominator;
        calculatedValue =  Math.min(calculatedValue, this.maxValue)
        calculatedValue =  Math.max(calculatedValue, this.minValue)
      }

      return calculatedValue;
    },
    calculateDistance: (x1, y1, x2, y2) => {
      return Math.sqrt(Math.pow(x2 - x1, 2) + Math.pow(y2 - y1, 2));
    },
  },
}
</script>

<style scoped lang="scss">
.contours {
  opacity: 0.5;
  transform: rotateX(180deg);
}
</style>



// WEBPACK FOOTER //
// contours.vue?48525ef2